import * as React from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, KeyRound, ShieldCheck, ShieldAlert, Trash2 } from 'lucide-react';
import type { MyProfile } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { avatarsKey } from '@/lib/avatars';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

export const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profile',
  component: ProfilePage,
});

const profileKey = ['my-profile'] as const;

function ProfilePage(): React.ReactElement {
  const profileQuery = useQuery({ queryKey: profileKey, queryFn: () => api.myProfile() });
  return (
    <AppShell>
      <div className="flex flex-col gap-2">
        <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">My profile</h1>
        <p className="text-base text-[hsl(var(--muted-foreground))]">
          How you appear to colleagues in chat, calls and mentions. Your employee record itself is
          managed by an Admin.
        </p>
      </div>
      {profileQuery.isPending ? (
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading…</p>
      ) : profileQuery.isError ? (
        <p className="text-sm text-[hsl(var(--destructive))]">{errorMessage(profileQuery.error)}</p>
      ) : (
        <ProfileBody profile={profileQuery.data} />
      )}
    </AppShell>
  );
}

function ProfileBody({ profile }: { profile: MyProfile }): React.ReactElement {
  const queryClient = useQueryClient();
  const [name, setName] = React.useState(profile.name);
  const [phone, setPhone] = React.useState(profile.phone ?? '');
  const [designation, setDesignation] = React.useState(profile.designation ?? '');
  const [about, setAbout] = React.useState(profile.about ?? '');
  const [saved, setSaved] = React.useState(false);
  const fileInput = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setName(profile.name);
    setPhone(profile.phone ?? '');
    setDesignation(profile.designation ?? '');
    setAbout(profile.about ?? '');
  }, [profile]);

  const applied = async (next: MyProfile): Promise<void> => {
    queryClient.setQueryData(profileKey, next);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['me'] }),
      queryClient.invalidateQueries({ queryKey: avatarsKey }),
      queryClient.invalidateQueries({ queryKey: ['chat'] }),
    ]);
  };

  const save = useMutation({
    mutationFn: () =>
      api.updateMyProfile({
        name: name.trim(),
        phone: phone.trim() || null,
        designation: designation.trim() || null,
        about: about.trim() || null,
      }),
    onSuccess: async (next) => {
      await applied(next);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2_500);
    },
  });
  const upload = useMutation({
    mutationFn: (file: File) => api.uploadMyAvatar(file),
    onSuccess: applied,
  });
  const remove = useMutation({ mutationFn: () => api.removeMyAvatar(), onSuccess: applied });

  const dirty =
    name.trim() !== profile.name ||
    (phone.trim() || null) !== profile.phone ||
    (designation.trim() || null) !== profile.designation ||
    (about.trim() || null) !== profile.about;
  const joined = new Date(`${profile.dateJoined}T00:00:00`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      <div className="flex flex-col gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Photo</CardTitle>
            <CardDescription>Shown beside your messages and on calls. PNG, JPEG or WebP, up to 2 MB.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-5">
            <Avatar name={profile.name} src={profile.avatarUrl} size="2xl" />
            <div className="flex flex-wrap gap-2">
              <input
                ref={fileInput}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="sr-only"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) upload.mutate(file);
                  e.target.value = '';
                }}
              />
              <Button variant="outline" onClick={() => fileInput.current?.click()} disabled={upload.isPending}>
                <Camera />
                {upload.isPending ? 'Uploading…' : profile.avatarUrl ? 'Change photo' : 'Upload photo'}
              </Button>
              {profile.avatarUrl ? (
                <Button variant="ghost" onClick={() => remove.mutate()} disabled={remove.isPending}>
                  <Trash2 />
                  Remove
                </Button>
              ) : null}
            </div>
            {upload.isError ? (
              <p className="basis-full text-sm text-[hsl(var(--destructive))]">{errorMessage(upload.error)}</p>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (dirty && name.trim()) save.mutate();
            }}
          >
            <CardHeader>
              <CardTitle>About you</CardTitle>
              <CardDescription>Colleagues see these in the chat directory and on your messages.</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <Field label="Name" required>
                <Input aria-label="Name" value={name} onChange={(e) => setName(e.target.value)} maxLength={160} />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Designation" hint="e.g. Senior Developer">
                  <Input
                    aria-label="Designation"
                    value={designation}
                    onChange={(e) => setDesignation(e.target.value)}
                    maxLength={120}
                  />
                </Field>
                <Field label="Phone" hint="Shown to colleagues only">
                  <Input
                    aria-label="Phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    maxLength={32}
                    inputMode="tel"
                  />
                </Field>
              </div>
              <Field label="About" hint={`${about.length}/500`}>
                <Textarea
                  aria-label="About"
                  value={about}
                  onChange={(e) => setAbout(e.target.value.slice(0, 500))}
                  placeholder="What you work on, where you sit, hours you keep."
                />
              </Field>
              {save.isError ? (
                <p className="text-sm text-[hsl(var(--destructive))]">{errorMessage(save.error)}</p>
              ) : null}
              <div className="flex items-center gap-3">
                <Button type="submit" disabled={!dirty || !name.trim() || save.isPending}>
                  {save.isPending ? 'Saving…' : 'Save changes'}
                </Button>
                {saved ? <span className="text-sm text-[hsl(var(--muted-foreground))]">Saved.</span> : null}
              </div>
            </CardContent>
          </form>
        </Card>
      </div>

      <div className="flex flex-col gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Employee record</CardTitle>
            <CardDescription>Managed in People &amp; Access by an Admin.</CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-[hsl(var(--muted-foreground))]">Employee ID</dt>
              <dd className="font-mono">{profile.employeeId ?? '—'}</dd>
              <dt className="text-[hsl(var(--muted-foreground))]">Email</dt>
              <dd className="break-all">{profile.email}</dd>
              <dt className="text-[hsl(var(--muted-foreground))]">Joined</dt>
              <dd>{joined}</dd>
              <dt className="text-[hsl(var(--muted-foreground))]">Roles</dt>
              <dd className="flex flex-wrap gap-1">
                {profile.roles.map((r) => (
                  <Badge key={r} variant="secondary">
                    {r}
                  </Badge>
                ))}
                {profile.csuiteRole ? <Badge>{profile.csuiteRole}</Badge> : null}
              </dd>
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Security</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 text-sm">
            <div className="flex items-center gap-2">
              {profile.twoFactorVerified ? (
                <ShieldCheck className="size-4 text-[hsl(var(--brand))]" />
              ) : (
                <ShieldAlert className="size-4 text-[hsl(var(--muted-foreground))]" />
              )}
              <span>{profile.twoFactorVerified ? 'Two-factor sign-in is on.' : 'Two-factor sign-in is not set up.'}</span>
            </div>
            {!profile.twoFactorVerified ? (
              <Link to="/2fa/setup" className="font-medium text-[hsl(var(--brand))] hover:underline">
                Set up two-factor
              </Link>
            ) : null}
            <Link to="/change-password" className="flex items-center gap-2 font-medium text-[hsl(var(--brand))] hover:underline">
              <KeyRound className="size-4" />
              Change password
            </Link>
            <Link to="/settings/notifications" className="font-medium text-[hsl(var(--brand))] hover:underline">
              Notification preferences
            </Link>
            <Link to="/settings/api-tokens" className="font-medium text-[hsl(var(--brand))] hover:underline">
              API tokens
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
